/**
 * Maps GET /ats_interviews/:id and GET /ats_schedules/:id responses
 * to the wizard's internal payload shape (edit mode).
 *
 * Also exports hydrateCommunicationFromApi which is shared with
 * fromPlan.ts — both edit and create endpoints emit the same comm field shapes.
 *
 * Pure functions — no hooks, no side effects.
 */
import type { InterviewDetails } from '@/api/useInterviews';
import type { ScheduleInterviewPayload } from '../config/types';
import type { FindTimeValue } from '../calendar/selfSchedule';
import { coerceBufferMinutes } from '../calendar/selfSchedule/buffer';
import type { ScheduleFeatureFlags } from '../payload/types';
import { SHARED_EMAIL_VALUE_PREFIX } from '../hooks/useScheduleSenderOptions';

// ── Helpers ────────────────────────────────────────────────────────────────

interface HydratedSelfScheduleSlot {
  start: string;
  end: string;
  duration: number;
  is_custom: boolean;
  override_type?: string;
  isBooked?: boolean;
}

function minimumNoticeMs(
  interview: InterviewDetails | Record<string, unknown>
): number {
  const enabled = (interview as Record<string, unknown>).minimum_notice_enabled;
  if (enabled === false) return 0;
  const cfg = (interview as Record<string, unknown>).minimum_notice_config as
    | { value?: number; type?: 'hours' | 'days' }
    | undefined;
  if (!cfg || typeof cfg.value !== 'number' || cfg.value <= 0) return 0;
  const perUnitMs = cfg.type === 'days' ? 86_400_000 : 3_600_000;
  return cfg.value * perUnitMs;
}

function pickValidSelfScheduleSlots(
  interview: InterviewDetails,
  duration: number,
  nowMs: number = Date.now()
): HydratedSelfScheduleSlot[] {
  if (!Array.isArray(interview.time_slots)) return [];
  const noticeMs = minimumNoticeMs(interview);
  const cutoffMs = nowMs + noticeMs;
  const out: HydratedSelfScheduleSlot[] = [];
  for (const slot of interview.time_slots) {
    const start = slot.start_at ?? slot.start_time ?? '';
    const end = slot.end_at ?? slot.end_time ?? '';
    if (!start || !end) continue;
    const startMs = Date.parse(start);
    const endMs = Date.parse(end);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    if (endMs <= nowMs) continue;
    if (startMs < cutoffMs) continue;
    out.push({
      start,
      end,
      duration,
      is_custom: Boolean(slot.is_custom),
      ...(slot.override_type ? { override_type: slot.override_type } : {}),
    });
  }
  return out;
}

function findTimeValueFromScheduleType(
  scheduleType: string | undefined
): FindTimeValue {
  switch (scheduleType) {
    case 'Next weekday':
      return 'next_weekday';
    case 'Next 2 weekdays':
      return 'next_2_weekdays';
    case 'Next 3 weekdays':
      return 'next_3_weekdays';
    case 'Next 4 weekdays':
      return 'next_4_weekdays';
    case 'Next 5 weekdays':
      return 'next_5_weekdays';
    case 'Next week':
      return 'next_week';
    case 'Next 2 weeks':
      return 'next_2_weeks';
    default:
      return null;
  }
}

// ── Details ────────────────────────────────────────────────────────────────

export function hydrateDetailsFromApi(
  interview: InterviewDetails,
  nowMs: number = Date.now(),
  featureFlags?: ScheduleFeatureFlags
): Record<string, unknown> {
  const participants = interview.participants ?? [];
  const participantIds =
    interview.participant_ids ?? participants.map((p) => p.id);

  let duration =
    typeof interview.duration === 'number' && interview.duration > 0
      ? interview.duration
      : 30;
  if (
    !(typeof interview.duration === 'number' && interview.duration > 0) &&
    typeof interview.start_time === 'string' &&
    typeof interview.end_time === 'string'
  ) {
    const startMs = Date.parse(interview.start_time);
    const endMs = Date.parse(interview.end_time);
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      duration = Math.round((endMs - startMs) / 60000);
    }
  }
  // Clamp to the form's accepted range. The previous wizard allowed
  // `duration > 0`; the new floor is 5. Without this, opening a
  // legacy interview saved at 1–4 minutes for an unrelated edit
  // (participants, location, etc.) would fail validation on save
  // even though the user never touched the duration field.
  if (duration < 5) duration = 5;
  if (duration > 1440) duration = 1440;

  const hrNested = interview.hackerrank_interview as
    | { ext_interview_template_id?: string | number }
    | undefined;
  const hrTemplate =
    hrNested?.ext_interview_template_id ??
    (interview.ext_interview_template_id as string | number | undefined);
  // Empty → '' (single-select Zod is `z.string()`; null leaks a raw
  // type-mismatch error instead of the required message).
  const ext_interview_template_id =
    hrTemplate !== undefined && hrTemplate !== null ? String(hrTemplate) : '';

  // host / ats_office come back as nested objects on the show endpoint
  // (`host: { id }`, `ats_office: { id }`) — there is no flat
  // `host_id` / `ats_office_id` in the response. Without the nested
  // fallback, the form fields render empty on edit even though the
  // interview has them set on the server.
  const rawHostId =
    interview.host_id !== undefined && interview.host_id !== null
      ? interview.host_id
      : interview.host?.id;
  const rawOfficeId =
    interview.ats_office_id !== undefined && interview.ats_office_id !== null
      ? interview.ats_office_id
      : interview.ats_office?.id;

  return {
    interviewName: interview.name ?? '',
    interview_type: interview.kind ?? interview.interview_kind ?? 'one_on_one',
    participant_ids: participantIds.map(String),
    duration,
    location: interview.location ?? 'zoom',
    host_id:
      rawHostId !== undefined && rawHostId !== null ? String(rawHostId) : '',
    ats_office_id:
      rawOfficeId !== undefined && rawOfficeId !== null
        ? String(rawOfficeId)
        : '',
    calendar_visibility:
      interview.calendar_visibility === 'private' ||
      interview.calendar_visibility === true,
    calendar_info: (() => {
      const scheduled = interview.scheduled_calendar as
        | { provider_calendar_id?: string; is_primary?: boolean }
        | undefined;
      if (scheduled?.provider_calendar_id) {
        return {
          provider_calendar_id: scheduled.provider_calendar_id,
          is_primary: Boolean(scheduled.is_primary),
        };
      }
      const raw = (interview as Record<string, unknown>).calendar_info as
        | { provider_calendar_id?: string; is_primary?: boolean }
        | undefined;
      return raw?.provider_calendar_id
        ? {
            provider_calendar_id: raw.provider_calendar_id,
            is_primary: Boolean(raw.is_primary),
          }
        : null;
    })(),
    enable_note_taker: Boolean(interview.note_taker_enabled),
    book_meeting_room:
      Array.isArray(interview.meeting_rooms) &&
      interview.meeting_rooms.length > 0,
    // isMulti: false on the meeting-room Select — seed as a single string,
    // not an array. Multiple rooms are unsupported in the UI; take the first.
    meeting_room_ids: interview.meeting_rooms?.[0]?.email ?? '',
    ...(interview.start_time && interview.end_time
      ? {
          bookedSlot: {
            start: interview.start_time,
            // If the hydrated duration was clamped above (out of the
            // accepted [5, 1440] range), recompute end_time from
            // start + duration*60_000 so the form's slot span and
            // duration field stay consistent. Without this, save would
            // ship the original end_time and the wire payload would
            // contradict the form-displayed duration.
            end: (() => {
              const startMs = Date.parse(interview.start_time);
              const endMs = Date.parse(interview.end_time);
              if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
                return interview.end_time;
              }
              const intendedEndMs = startMs + duration * 60_000;
              return intendedEndMs === endMs
                ? interview.end_time
                : new Date(intendedEndMs).toISOString();
            })(),
            duration,
          },
        }
      : {}),
    selfScheduleSlots: pickValidSelfScheduleSlots(interview, duration, nowMs),
    findTimeValue: findTimeValueFromScheduleType(
      typeof interview.schedule_type === 'string'
        ? interview.schedule_type
        : undefined
    ),
    // Buffer config — hydrate when EITHER buffer flag is on
    // (`feInterviewBuffer` = self-schedule, `feManualInterviewBuffer` =
    // manual/adhoc edit). With both off the wizard stays on the default
    // 15/15/true shared-state values and no buffer fields appear in
    // payloadRef. BE shape (kula/core CORE-4350): flat per-audience
    // fields — interviewer_buffer_{enabled,pre_minutes,post_minutes}.
    // The FE's single buffer control maps to the interviewer audience.
    ...((): Record<string, number | boolean> => {
      if (
        !featureFlags?.feInterviewBuffer &&
        !featureFlags?.feManualInterviewBuffer
      )
        return {};
      const raw = interview as Record<string, unknown>;
      const out: Record<string, number | boolean> = {};
      // Coerce: the manual `/ats_interviews` FormData path stores minutes
      // as strings ("13"), and a mis-seeded record can carry the literal
      // "pre_minutes" — `coerceBufferMinutes` returns a finite number or
      // undefined, so bad values fall back to the wizard defaults.
      const pre = coerceBufferMinutes(raw.interviewer_buffer_pre_minutes);
      if (pre !== undefined) out.bufferBeforeMinutes = pre;
      const post = coerceBufferMinutes(raw.interviewer_buffer_post_minutes);
      if (post !== undefined) out.bufferAfterMinutes = post;
      if (typeof raw.interviewer_buffer_enabled === 'boolean')
        out.bufferEnabled = raw.interviewer_buffer_enabled;
      return out;
    })(),
    participant_default_options: participants.map((p) => ({
      value: String(p.id),
      label: p.name ?? p.email ?? `User ${p.id}`,
      // Email surfaces in `renderInterviewerOptionCell` (dropdown row)
      // — same field `fromPlan.ts` sets, so the edit-mode dropdown
      // matches create mode.
      description: p.email,
      image: p.avatar ?? p.avatar_url ?? p.avatar_thumb_url,
      zoomStatus: {
        can_host_zoom_meeting: p.can_host_zoom_meeting,
        zoom_rate_limit_reached: p.zoom_rate_limit_reached,
        zoom_plan: p.zoom_plan,
        zoom_plan_type: p.zoom_plan_type,
      },
      meetEligible: p.can_host_gmeet_meeting,
      // Carry the interviewer's timezone into the chip renderer's
      // `data.timezone` lookup (parity with `fromPlan.ts:181`). Without
      // this, edit-mode chips show the missing-timezone globe icon
      // even when the BE has a timezone stored for the user.
      data: { timezone: p.time_zone ?? '' },
    })),
    ext_interview_template_id,
  };
}

// ── Communication (shared with fromPlan.ts) ────────────────────────────────

interface BackendCommTemplate {
  id?: number;
  name?: string | null;
  subject?: string | null;
  body?: string | null;
  attachments?: unknown[];
}

interface BackendParticipant {
  id?: number;
}

/**
 * Two key shapes can arrive depending on backend version / endpoint:
 *   - new shape (CORE-4040): `sender: { id }`, `cc_participants: [{id,...}]`,
 *     `bcc_participants: [{id,...}]`
 *   - legacy: flat `from_user_id`, `cc_user_ids`, `bcc_user_ids`
 * Hydration accepts either so an existing-interview's sender survives the
 * round-trip and the From dropdown prefills correctly.
 */
interface BackendEmailCommConfig {
  subject?: string | null;
  body?: string | null;
  attachments?: unknown[];
  comm_template_id?: number;
  from_user_id?: number;
  cc_user_ids?: number[];
  bcc_user_ids?: number[];
  sender?: BackendParticipant | null;
  cc_participants?: BackendParticipant[];
  bcc_participants?: BackendParticipant[];
  ats_shared_email?: { id?: number; email?: string; name?: string } | null;
}

function extractParticipantIds(
  participants: BackendParticipant[] | undefined,
  legacyIds: number[] | undefined
): number[] {
  if (Array.isArray(participants) && participants.length > 0) {
    return participants
      .map((p) => p?.id)
      .filter((id): id is number => typeof id === 'number');
  }
  return Array.isArray(legacyIds) ? legacyIds : [];
}

/**
 * Shared by both edit (GET /ats_interviews/:id) and create
 * (GET /ats_interviews/new) — both endpoints emit the same comm field shapes.
 */
export function hydrateCommunicationFromApi(
  interview: InterviewDetails | Record<string, unknown>
): Record<string, unknown> {
  const asRecord = interview as Record<string, unknown>;
  const candidateCal = asRecord.candidate_comm_template as
    | BackendCommTemplate
    | undefined;
  const reviewerCal = asRecord.reviewer_comm_template as
    | BackendCommTemplate
    | undefined;
  const candidateEmail = asRecord.candidate_email_config as
    | BackendEmailCommConfig
    | undefined;
  const interviewerEmail = asRecord.interviewer_email_config as
    | BackendEmailCommConfig
    | undefined;
  const selfScheduleEmail = asRecord.candidate_self_schedule_email_config as
    | BackendEmailCommConfig
    | undefined;

  const toCommConfig = (t: BackendCommTemplate | undefined) =>
    t
      ? {
          subject: t.subject ?? '',
          body: t.body ?? '',
          attachments: Array.isArray(t.attachments) ? t.attachments : [],
          comm_template_id: t.id,
        }
      : undefined;

  const toEmailCommConfig = (e: BackendEmailCommConfig | undefined) => {
    if (!e || Object.keys(e).length === 0) return undefined;
    // Prefer the new participant-object shape (sender / cc_participants /
    // bcc_participants — see CORE-4040 jbuilder); fall back to the legacy
    // flat ids. Without this, an existing interview's saved sender is
    // silently dropped here and the From field can't prefill.
    //
    // Emit the form-synced key shape (`from` / `cc` / `bcc`) — that's the
    // shape `commConfigToEmailComposition` reads when CommunicationStep
    // mounts. Producing `from_user_id` / `cc_user_ids` here would land
    // in the payload but never reach the email reducer's `from` field.
    const senderId =
      typeof e.sender?.id === 'number' ? e.sender.id : e.from_user_id;
    const sharedEmailId =
      typeof e.ats_shared_email?.id === 'number' && e.ats_shared_email.id > 0
        ? e.ats_shared_email.id
        : undefined;
    return {
      subject: e.subject ?? '',
      body: e.body ?? '',
      attachments: Array.isArray(e.attachments) ? e.attachments : [],
      comm_template_id: e.comm_template_id,
      from: senderId,
      ats_shared_email_id: sharedEmailId,
      cc: extractParticipantIds(e.cc_participants, e.cc_user_ids),
      bcc: extractParticipantIds(e.bcc_participants, e.bcc_user_ids),
    };
  };

  return {
    send_calendar_invite_to_candidate: Boolean(
      asRecord.send_calendar_invite_to_candidate ?? true
    ),
    send_calendar_invite_to_interviewer: Boolean(
      asRecord.send_calendar_invite_to_interviewer ?? true
    ),
    send_email_to_candidate: Boolean(asRecord.send_email_to_candidate),
    send_email_to_interviewer: Boolean(asRecord.send_email_to_interviewer),
    send_ics_to_candidate: Boolean(asRecord.send_ics_to_candidate),
    send_ics_to_interviewer: Boolean(asRecord.send_ics_to_interviewer),
    calendar_invite_candidate: toCommConfig(candidateCal),
    calendar_invite_interviewer: toCommConfig(reviewerCal),
    candidate_email_comm_config: toEmailCommConfig(candidateEmail),
    interviewer_email_comm_config: toEmailCommConfig(interviewerEmail),
    candidate_self_schedule_email_comm_config:
      toEmailCommConfig(selfScheduleEmail),
    self_schedule_section_enabled: Boolean(
      asRecord.self_schedule_section_enabled
    ),
  };
}

// ── Scorecard ──────────────────────────────────────────────────────────────

function hydrateScorecardFromApi(
  interview: InterviewDetails
): Record<string, unknown> {
  const cfg = (interview.scorecard_config as
    | { questions?: unknown[]; is_feedback_mandatory?: boolean }
    | undefined) ?? { questions: [], is_feedback_mandatory: false };
  return {
    enabled: Boolean(interview.scorecard_required),
    ai_enabled: Boolean(interview.fill_with_ai_enabled),
    template_id: '',
    is_feedback_mandatory: Boolean(cfg.is_feedback_mandatory),
    questions: Array.isArray(cfg.questions) ? cfg.questions : [],
    feedbackDescription:
      (interview.scorecard_feedback_description as string | undefined) ?? '',
    ratingDescription:
      (interview.scorecard_rating_description as string | undefined) ?? '',
  };
}

// ── Slot selection email ───────────────────────────────────────────────────

/**
 * Maps the slot-selection email fields from GET /ats_schedules/:id.
 * Returns undefined when the response has no template (step uses defaults).
 *
 * Key: reads candidate_mail_comm_template (not candidate_email_config —
 * that is the post-booking interview notice, belonging to Communication).
 */
function hydrateSlotSelectionEmailFromApi(
  interview: InterviewDetails | Record<string, unknown>
): Record<string, unknown> | undefined {
  const asRecord = interview as Record<string, unknown>;
  const template = asRecord.candidate_mail_comm_template as
    | {
        id?: number;
        subject?: string | null;
        body?: string | null;
        attachments?: unknown[];
      }
    | undefined;
  const from = asRecord.candidate_mail_from as
    | { id?: number | string }
    | undefined;
  // The /ats_schedules/:id jbuilder emits the slot-selection email's
  // shared mailbox at the response top level under the key
  // `ats_shared_email` (NOT `candidate_mail_ats_shared_email`). The
  // earlier audit fix used the wrong key name here, so the round-trip
  // silently fell back to `from` without ever preselecting the shared
  // mailbox. Verified against
  // app/views/api_ats/v1/ats_schedules/_ats_schedule.json.jbuilder L69
  // on `origin/CORE-4039`.
  const sharedEmail = asRecord.ats_shared_email as
    | { id?: number | string }
    | null
    | undefined;
  const cc = asRecord.candidate_mail_cc_participants as
    | Array<{ id?: number }>
    | undefined;
  const bcc = asRecord.candidate_mail_bcc_participants as
    | Array<{ id?: number }>
    | undefined;
  if (!template || typeof template !== 'object') return undefined;
  // Shared-mailbox sender round-trip: the slot-email step writes
  // `candidate_mail_ats_shared_email_id` on save (mapSlotEmail) but
  // the GET response surfaces the shared mailbox under the relation
  // key. Read it back as `shared:${id}` so the From dropdown
  // preselects the shared mailbox on edit instead of falling back to
  // the proxy user that was sent as `candidate_mail_from`.
  const sharedId =
    sharedEmail && sharedEmail.id !== undefined && sharedEmail.id !== null
      ? Number(sharedEmail.id)
      : undefined;
  const fromValue =
    sharedId !== undefined && Number.isFinite(sharedId) && sharedId > 0
      ? `${SHARED_EMAIL_VALUE_PREFIX}${sharedId}`
      : from && from.id !== undefined && from.id !== null
        ? String(from.id)
        : '';
  return {
    subject: template.subject ?? '',
    content: template.body ?? '',
    from: fromValue,
    cc: Array.isArray(cc)
      ? cc.map((u) => (u.id !== undefined ? String(u.id) : '')).filter(Boolean)
      : [],
    bcc: Array.isArray(bcc)
      ? bcc.map((u) => (u.id !== undefined ? String(u.id) : '')).filter(Boolean)
      : [],
    template: null,
    _hydrated_template_id: template.id ?? null,
  };
}

// ── Main export ────────────────────────────────────────────────────────────

export function hydratePayloadFromApi(
  interview: InterviewDetails,
  nowMs: number = Date.now(),
  featureFlags?: ScheduleFeatureFlags
): ScheduleInterviewPayload {
  const slotSelectionEmail = hydrateSlotSelectionEmailFromApi(interview);
  return {
    details: hydrateDetailsFromApi(interview, nowMs, featureFlags),
    communication: hydrateCommunicationFromApi(interview),
    scorecard: hydrateScorecardFromApi(interview),
    ...(slotSelectionEmail ? { slotSelectionEmail } : {}),
  };
}
